// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import { convertTemperature, formatValueWithUnit } from '../../../../../../plugins/general';

// Store
import { getMeasurementUnit } from '../../../../../../store/measurement.store';

// Types
import { MeasurementType, TemperatureType } from '../../../../../../types/global';

// Components
import { EquipmentSensorListItem } from '../../../../../../components/EquipmentSensorList';
import { Col } from '../../../../../../layout/elements/Col';
import { Row } from '../../../../../../layout/elements/Row';

// Modules
import { ListModule } from '../ListModule';

// Component
import { SensorScalingProps } from './SensorScaling.types';
import {
  Container,
  ListContainer,
  Separator,
} from './SensorScaling.style';

function SensorScaling({
  className,
  testId,
  unitData,
}: SensorScalingProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  const temperatureUnit: TemperatureType = getMeasurementUnit(MeasurementType.temperature);

  const crusherItems: EquipmentSensorListItem[] = [
    {
      sensor: t('view.crusher.vsi.maintenance.sensorScaling.crusherSensors.mainMotorCurrent'),
      min: formatValueWithUnit(
        unitData?.spCrusherMotorAmpsScaleMin,
        '',
        0,
      ),
      max: formatValueWithUnit(
        unitData?.spCrusherMotorAmpsScaleMax,
        'A',
        0,
      ),
    },
    {
      sensor: t('view.crusher.vsi.maintenance.sensorScaling.crusherSensors.motorRpm'),
      min: formatValueWithUnit(
        unitData?.spCrusherMotorRpmScaleMin,
        '',
        0,
      ),
      max: formatValueWithUnit(
        unitData?.spCrusherMotorRpmScaleMax,
        'RPM',
        0,
      ),
    },
    {
      sensor: t('view.crusher.vsi.maintenance.sensorScaling.crusherSensors.vibrationMonitors'),
      min: formatValueWithUnit(
        unitData?.spVibScaleMin,
        '',
        0,
      ),
      max: formatValueWithUnit(
        unitData?.spVibScaleMax,
        '%',
        0,
      ),
    },
    {
      sensor: t('view.crusher.vsi.maintenance.sensorScaling.crusherSensors.crusherVfdHz'),
      min: formatValueWithUnit(
        unitData?.spVFDHzScaleMin,
        '',
        0,
      ),
      max: formatValueWithUnit(
        unitData?.spVFDHzScaleMax,
        'Hz',
        0,
      ),
    },
  ];

  const lubricationItems: EquipmentSensorListItem[] = [
    {
      sensor: t('view.crusher.vsi.maintenance.sensorScaling.lubricationSensors.tankTemperature'),
      min: convertTemperature(
        unitData?.spTankTempScaleMin,
        temperatureUnit,
        0,
        true,
      ).value,
      max: convertTemperature(
        unitData?.spTankTempScaleMax,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: t('view.crusher.vsi.maintenance.sensorScaling.lubricationSensors.returnTemperature'),
      min: convertTemperature(
        unitData?.spReturnTempScaleMin,
        temperatureUnit,
        0,
        true,
      ).value,
      max: convertTemperature(
        unitData?.spReturnTempScaleMax,
        temperatureUnit,
        0,
        true,
      ).formattedValue,
    },
    ...(unitData?.lubeSupplyTempEnable ? [
      {
        sensor: t('view.crusher.vsi.maintenance.sensorScaling.lubricationSensors.supplyTemperature'),
        min: convertTemperature(
          unitData?.spSupplyTempScaleMin,
          temperatureUnit,
          0,
          true,
        ).value,
        max: convertTemperature(
          unitData?.spSupplyTempScaleMax,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
    ] : []),
  ];

  return (
    <Container className={className} data-testid={testId}>
      <Row>
        <Col>
          <Separator />
          <Row>
            <Col cols={12}>
              <ListContainer>
                <ListModule
                  title={t('view.crusher.vsi.maintenance.sensorScaling.crusherSensors.title')}
                  items={crusherItems}
                />
              </ListContainer>
            </Col>
            <Col cols={12}>
              <ListContainer>
                <ListModule
                  title={t('view.crusher.vsi.maintenance.sensorScaling.lubricationSensors.title')}
                  items={lubricationItems}
                />
              </ListContainer>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
}

export { SensorScaling };
